import { useFileViewer, useSelectedUnit, useServices } from '@/app/providers';
import { useRoleLabel } from '@/shared/hooks';
import { useI18n } from '@/shared/i18n';
import type { Unit } from '@yawi/types';
import { Check, ChevronDown, Home, LogOut, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const getInitials = (name: string) =>
  name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();

const getUnitLabel = (unit: Unit): string => {
  if (unit.name) return unit.name;
  if (unit.tower) return `Torre ${unit.tower} - ${unit.number || unit.floor || ''}`.trim();
  return `${unit.street || ''} #${unit.exteriorNumber || unit.number || ''}`.trim() || 'Unidad';
};

export function UserMenuToggleV2() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { currentUser, logout } = useServices();
  const { openFileByUrl } = useFileViewer();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { selectedUnit, selectedUnitId, setSelectedUnitId, hasMultipleUnits } = useSelectedUnit();
  const { getLabel } = useRoleLabel();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!currentUser) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-3 p-3 w-full bg-surface-secondary rounded-xl cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors border border-theme-light"
      >
        {currentUser.avatarUrl ? (
          <div
            className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-slate-200 hover:ring-primary-400 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              openFileByUrl(currentUser.avatarUrl, 'avatar.jpg', 'image/jpeg');
            }}
          >
            <img src={currentUser.avatarUrl} alt={currentUser.name} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-12 h-12 flex items-center justify-center bg-primary-600 text-white text-2xl font-bold rounded-full flex-shrink-0">
            {getInitials(currentUser.name)}
          </div>
        )}
        <div className="overflow-hidden flex-1 text-left">
          <p className="font-medium text-sm truncate text-theme">{currentUser.name}</p>
          <p className="text-xs text-theme-tertiary truncate">
            {hasMultipleUnits && selectedUnit
              ? getUnitLabel(selectedUnit)
              : currentUser.residenceInfo
                ? `${currentUser.residenceInfo.tower} - ${currentUser.residenceInfo.unit}`
                : getLabel(currentUser.tenantRole || currentUser.role)}
          </p>
        </div>
        <ChevronDown size={14} className={`text-theme-secondary flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 glass-heavy rounded-xl shadow-xl border border-theme-light py-1 z-50">
          {hasMultipleUnits && currentUser.units && (
            <>
              <p className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-theme-tertiary">
                Cambiar de unidad
              </p>
              {currentUser.units.map((assignment) => {
                const isActive = assignment.unitId === selectedUnitId;
                return (
                  <button
                    key={assignment.unitId}
                    onClick={() => { setSelectedUnitId(assignment.unitId); setOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? 'text-primary-600 dark:text-primary-400 font-medium'
                        : 'text-theme-secondary hover:bg-surface-secondary hover:text-theme'
                    }`}
                  >
                    <Home size={15} className="shrink-0" />
                    <span className="flex-1 text-left truncate">{getUnitLabel(assignment.unit)}</span>
                    {isActive && <Check size={14} className="shrink-0" />}
                  </button>
                );
              })}
              <div className="my-1 border-t border-theme-light" />
            </>
          )}
          <button
            onClick={() => { navigate('/perfil'); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-theme-secondary hover:bg-surface-secondary hover:text-theme transition-colors"
          >
            <User size={15} />
            {t('menu.v2.myProfile')}
          </button>

          <div className="my-1 border-t border-theme-light" />
          <button
            onClick={() => { logout(); setOpen(false); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <LogOut size={15} />
            {t('menu.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
